import { useState, useEffect } from "react";
import { apiGet, apiPut } from "../../utils/apiClient";
import CancelActionModal from "./modal/CancelActionModal";
import "./css/cancelRequest.css";


const CancelRequest = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkedIds, setCheckedIds] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIsAccept, setModalIsAccept] = useState(true);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;

  const loadList = async (page = 0) => {
    setLoading(true);
    try {
      const response = await apiGet(`/store/order/cancel?page=${page}&size=${pageSize}`);
      const data = await response.json();
      if (response.ok) {
        setCurrentPage(data.number || 0);
        setTotalPages(data.totalPages || 1);
        setTotalElements(data.totalElements || 0);
        setList(data.content || []);
      }
    } catch (error) {
      console.error("취소 요청 조회 중 오류 발생:", error);
    } finally {
      setLoading(false);
    }
    setCheckedIds([]);
  };

  useEffect(() => {
    loadList();
  }, []);

  const EXPIRE_DAYS = 5;
  const URGENT_DAYS = 2;

  // 요청일시 + 5일 = 만료일. 오늘로부터 5일 이내면 D-n, 그 외는 만료. 남은 일수가 2일 이하이면 urgent
  const formatYYMMDD = (date) => {
    const yy = String(date.getFullYear()).slice(2);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };

  // D-n 범위(0~5일)면 날짜 없이 D-n만, 만료면 만료일자 + 만료 배지
  const getExpireInfo = (dateTime) => {
    const empty = { requestText: "-", text: "-", label: "", urgent: false };
    if (!dateTime) return empty;
    const start = new Date(dateTime);
    if (isNaN(start)) return empty;

    const expire = new Date(start.getFullYear(), start.getMonth(), start.getDate() + EXPIRE_DAYS);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const remain = Math.round((expire - today) / (24 * 60 * 60 * 1000));

    const inRange = remain >= 0 && remain <= EXPIRE_DAYS;
    return {
      requestText: formatYYMMDD(start),
      text: inRange ? "" : formatYYMMDD(expire),
      label: inRange ? `D-${remain}` : "만료",
      urgent: inRange && remain <= URGENT_DAYS,
    };
  };

  /* ── 체크박스 ── */
  const isAllChecked = list.length > 0 && checkedIds.length === list.length;

  const toggleAll = () => {
    if (isAllChecked) {
      setCheckedIds([]);
    } else {
      setCheckedIds(list.map((item) => item.orderDetailIdx));
    }
  };

  const toggleOne = (id) => {
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  /* ── 승인 / 거절 모달 열기 ── */
  const handleBulkAction = (isAccept) => {
    if (checkedIds.length === 0) {
      alert("처리할 요청을 선택해주세요.");
      return;
    }
    setModalIsAccept(isAccept);
    setModalOpen(true);
  };

  /* ── 모달 확정 후 실제 처리 ── */
  const handleModalConfirm = async (reason) => {
    const action = modalIsAccept ? "승인" : "거절";
    setModalOpen(false);

    try {
      const response = await apiPut("/store/order/cancel", {
        items: checkedIds.map((id) => ({
          orderDetailIdx: id,
          reason: (reason && reason[id]) ? reason[id] : "",
        })),
        isApprove: modalIsAccept,
      });
      if (response.ok) {
        alert(`${checkedIds.length}건이 ${action}되었습니다.`);
        loadList(currentPage);
      } else {
        alert(`${action} 처리에 실패했습니다.`);
      }
    } catch (error) {
      console.error(`${action} 처리 중 오류:`, error);
      alert(`${action} 처리 중 오류가 발생했습니다.`);
    }
    setCheckedIds([]);
  };

  return (
    <div className="cancelrequest-wrap">
      {/* 헤더 */}
      <div className="cancelrequest-header">
        <div className="cancelrequest-header-title">
          취소 요청
        </div>
      </div>

      {/* 카운트 + 버튼 영역 */}
      <div className="cancelrequest-toolbar">
        <span className="cancelrequest-count">전체 {totalElements}건</span>
        <div className="cancelrequest-actions">
          <button
            className="cancelrequest-btn approve"
            onClick={() => handleBulkAction(true)}
          >
            승인
          </button>
          <button
            className="cancelrequest-btn reject"
            onClick={() => handleBulkAction(false)}
          >
            거절
          </button>
        </div>
      </div>

      {/* 테이블 */}
      <div className="cancelrequest-table-wrap">
        <table className="cancelrequest-table">
          <thead>
            <tr>
              <th className="cancelrequest-th-check">
                <label className="cancelrequest-checkbox">
                  <input
                    type="checkbox"
                    checked={isAllChecked}
                    onChange={toggleAll}
                  />
                  <span className="cancelrequest-checkbox-box" />
                </label>
              </th>
              <th>요청일자</th>
              <th>요청만료</th>
              <th>주문번호</th>
              <th>상품명</th>
              <th>수량</th>
              <th>가격</th>
              <th>사용여부</th>
              <th>구매자</th>
              <th>연락처</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="cancelrequest-empty">
                  로딩 중...
                </td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td colSpan="10" className="cancelrequest-empty">
                  취소 요청이 없습니다.
                </td>
              </tr>
            ) : (
              list.map((item, idx) => {
                const expire = getExpireInfo(item.requestDateTime);
                return (
                <tr
                  key={item.orderDetailIdx || idx}
                  className={`cancelrequest-row${checkedIds.includes(item.orderDetailIdx) ? " checked" : ""}${expire.urgent ? " urgent" : ""}`}
                >
                  <td>
                    <label className="cancelrequest-checkbox">
                      <input
                        type="checkbox"
                        checked={checkedIds.includes(item.orderDetailIdx)}
                        onChange={() => toggleOne(item.orderDetailIdx)}
                      />
                      <span className="cancelrequest-checkbox-box" />
                    </label>
                  </td>
                  <td>{expire.requestText}</td>
                  <td className={expire.urgent ? "cancelrequest-expire urgent" : "cancelrequest-expire"}>
                    {expire.text}
                    {expire.label && (
                      <span className={`cancelrequest-urgent-badge${expire.label === "만료" ? " expired" : expire.urgent ? "" : " normal"}`}>
                        {expire.label}
                      </span>
                    )}
                  </td>
                  <td>{item.orderCode}</td>
                  <td className="cancelrequest-td-left">{item.productName}</td>
                  <td>{item.quantity}</td>
                  <td>{item.price?.toLocaleString()}원</td>
                  <td>
                    <span className={`cancelrequest-used ${item.isUsed ? "used" : "unused"}`}>
                      {item.isUsed ? "사용" : "미사용"}
                    </span>
                  </td>
                  <td>{item.custName}</td>
                  <td>{item.custPhone}</td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      <div className="cancelrequest-footer">
        <div className="cancelrequest-pagination">
          <button
            className="cancelrequest-page"
            onClick={() => loadList(currentPage - 1)}
            disabled={currentPage === 0}
          >
            이전
          </button>
          {[...Array(Math.max(1, totalPages))].map((_, i) => (
            <button
              key={i}
              className={`cancelrequest-page${currentPage === i ? " active" : ""}`}
              onClick={() => loadList(i)}
            >
              {i + 1}
            </button>
          ))}
          <button
            className="cancelrequest-page"
            onClick={() => loadList(currentPage + 1)}
            disabled={totalPages <= 1 || currentPage >= totalPages - 1}
          >
            다음
          </button>
        </div>
      </div>

      {/* 승인/거절 처리 모달 */}
      {modalOpen && (
        <CancelActionModal
          isAccept={modalIsAccept}
          selectedItems={list.filter((item) => checkedIds.includes(item.orderDetailIdx))}
          onClose={() => setModalOpen(false)}
          onConfirm={handleModalConfirm}
        />
      )}
    </div>
  );
};

export default CancelRequest;